import { useEffect, useMemo, useState } from "react";
import OperationType from "../../../common/types/operation-type-enum";
import FormikFormFieldBuilder from "../../../common/formik-form-field/FormikFormFieldBuilder";
import { useTranslation } from "react-i18next";
import {
  useUpdateCustomerTrucksMutation,
  Vehicle,
} from "../../../../../generated/graphql";
import Notifier, {
  emptyNotification,
  NotificationMessage,
  NotificationType,
} from "src/app/pages/common/notifier/notifier";
import { formatApolloError } from "src/app/graphql/graphql-helpers";
import * as Yup from "yup";
import { useFormik } from "formik";
import { usePermission } from "src/app/hooks/permissions/use-permission";
import { UserPermissions } from "src/app/providers/permissions/permissions";

const UpsertCustomerTruck = (props: {
  customerId: number;
  selectedTruck: Vehicle;
  trucks: number[];
  operationType: OperationType;
  onOperationSuccess?: () => void;
  onClose: () => void;
  onStartEditClicked: () => void;
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canModifyPage = hasPermission([
    UserPermissions.CUSTOMER_MANAGEMENT.CREATE,
    UserPermissions.CUSTOMER_MANAGEMENT.EDIT,
  ]);

  let [notificationMessage, setNotificationMessage] =
    useState<NotificationMessage>();

  const selectedTruck = useMemo(
    () => (props.selectedTruck ? props.selectedTruck : ({} as Vehicle)),
    [props.selectedTruck]
  );

  const [
    updateCustomerTrucks,
    {
      loading: updatingCustomerTrucks,
      data: updateCustomerTrucksData,
      error: updateCustomerTrucksError,
      reset: resetUpdateCustomerTrucksData,
    },
  ] = useUpdateCustomerTrucksMutation();

  const onHideNotification = () => {
    if (notificationMessage?.type === NotificationType.success) {
      setTimeout(() => {
        if (props.onOperationSuccess) {
          props.onOperationSuccess();
          resetUpdateCustomerTrucksData();
        }
      }, 2);
    }
    setNotificationMessage(emptyNotification(notificationMessage));
  };

  useEffect(() => {
    if (updateCustomerTrucksError && !updatingCustomerTrucks) {
      let _err = updateCustomerTrucksError;
      setNotificationMessage({
        message: formatApolloError(_err),
        type: NotificationType.error,
      });
    }
  }, [updatingCustomerTrucks, updateCustomerTrucksError]);

  const newCustomerTruckSchema = Yup.object().shape({
    customerId: Yup.number(),
    id: Yup.number().required(
      t("modules.upsert.customer-truck.required.truck")
    ),
  });

  const formik = useFormik({
    initialValues: {
      customerId: props.customerId,
      id: selectedTruck.id ?? 1,
      vehicleNumber: selectedTruck.vehicleNo ?? "",
      eamId: selectedTruck.eamId ?? "",
      owner: selectedTruck.owner ?? "",
      active: selectedTruck.isActive ?? false,
    },
    validationSchema: newCustomerTruckSchema,
    onSubmit: (values) => {
      let newTrucksArrays = props.trucks;

      if (props.operationType === OperationType.delete) {
        newTrucksArrays = newTrucksArrays.filter(function (x) {
          return x !== values.id;
        });
      } else if (newTrucksArrays.includes(values.id)) {
        setNotificationMessage({
          message: t("modules.upsert.customer-truck.error.duplicate"),
          type: NotificationType.error,
        });

        return;
      } else {
        newTrucksArrays.push(values.id);
      }

      void updateCustomerTrucks({
        variables: {
          customerId: values.customerId,
          vehicles: newTrucksArrays,
        },
      });
    },
  });

  const { resetForm: formikResetForm, setValues: formikSetValues } = formik;

  useEffect(() => {
    formikSetValues({
      customerId: props.customerId,
      id: selectedTruck.id ?? 0,
      vehicleNumber: selectedTruck.vehicleNo ?? "",
      eamId: selectedTruck.eamId ?? "",
      owner: selectedTruck.owner ?? "",
      active: selectedTruck.isActive ?? false,
    });
  }, [
    formikSetValues,
    props.customerId,
    selectedTruck.eamId,
    selectedTruck.id,
    selectedTruck.isActive,
    selectedTruck.owner,
    selectedTruck.vehicleNo,
  ]);

  useEffect(() => {
    if (
      updateCustomerTrucksData?.upsertCustomerVehicle.customer?.id &&
      !updatingCustomerTrucks
    ) {
      let message = "";
      if (props.operationType === OperationType.create)
        message = t("modules.upsert.customer-truck.message.added");
      else if (props.operationType === OperationType.delete)
        message = t("modules.delete.customer-truck.message.deleted");

      setNotificationMessage({
        message: message,
        type: NotificationType.success,
      });

      void formikResetForm();
    }
  }, [
    updateCustomerTrucksData,
    updatingCustomerTrucks,
    formikResetForm,
    props.operationType,
    t,
  ]);

  const deleteTruckFromCustomer = () => {
    void formik.submitForm();
  };

  return (
    <div>
        <FormikFormFieldBuilder
          formik={formik}
          loading={updatingCustomerTrucks}
          itemName="Vehicle"
          onStartEditClicked={props.onStartEditClicked}
          selectedItem={selectedTruck}
          operationType={props.operationType}
          onClose={props.onClose}
          allowEdit={canModifyPage}
          onDeleteClicked={deleteTruckFromCustomer}
          fieldOptions={[
            {
              fieldName: "id",
              labelName: "Assignment of Vehicle",
              type: "vehicleFormSelect",
            },
          ]}
          excludeFields={["updatedAt", "createdAt"]}
          fieldConfig={{}}
        />
        <Notifier
          notificationMessage={notificationMessage}
          onHideNotifier={onHideNotification}
        />
    </div>
  );
};

export default UpsertCustomerTruck;
